/**
 * Reactive reads from the local mirror. Components re-render whenever the underlying
 * IndexedDB rows change (local edits, sync pulls, realtime).
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';
import {
  dateOnlyReminderTime,
  isCompleteType,
  resolveListStatuses,
  type Folder,
  type List,
  type NotificationLog,
  type Profile,
  type Reminder,
  type Space,
  type Status,
  type Tag,
  type Task,
  type Workspace,
} from '@karya/core';
import { db, type TaskActivity } from '@/db/dexie';
import { useSession } from '@/state/session';

const alive = <T extends { deleted_at: string | null }>(rows: T[]) =>
  rows.filter((r) => !r.deleted_at);

export const DEFAULT_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function fallbackProfile(userId: string | null, email: string | null): Profile {
  const ts = new Date(0).toISOString();
  return {
    id: userId ?? 'demo',
    email,
    display_name: email ? email.split('@')[0]! : 'there',
    timezone: DEFAULT_TZ,
    day_start_time: '09:00',
    daily_summary_enabled: true,
    daily_summary_time: null,
    date_only_reminder_time: null,
    default_quick_add_list_id: null,
    notification_email: email,
    email_enabled: true,
    push_enabled: true,
    theme: 'light',
    onboarded_at: null,
    default_reminders: null,
    next_days: 7,
    created_at: ts,
    updated_at: ts,
  };
}

export function useProfile(): Profile {
  const { userId, email } = useSession();
  const row = useLiveQuery(() => (userId ? db().profiles.get(userId) : undefined), [userId]);
  return useMemo(() => row ?? fallbackProfile(userId, email), [row, userId, email]);
}

/** Profile values used for time calculations. */
export function useTimeSettings() {
  const p = useProfile();
  return useMemo(
    () => ({
      tz: p.timezone || DEFAULT_TZ,
      dayStart: p.day_start_time.slice(0, 5),
      dateOnlyTime: dateOnlyReminderTime(p),
      nextDays: p.next_days || 7,
    }),
    [p],
  );
}

export interface WorkspaceIndex {
  loaded: boolean;
  workspaces: Workspace[];
  spaces: Space[];
  folders: Folder[];
  lists: List[];
  statuses: Status[];
  tags: Tag[];
  spaceById: Map<string, Space>;
  folderById: Map<string, Folder>;
  listById: Map<string, List>;
  statusById: Map<string, Status>;
  tagById: Map<string, Tag>;
  /** Resolved, ordered statuses for a list (own override or space defaults). */
  statusesForList: (listId: string) => Status[];
}

const EMPTY: WorkspaceIndex = {
  loaded: false,
  workspaces: [],
  spaces: [],
  folders: [],
  lists: [],
  statuses: [],
  tags: [],
  spaceById: new Map(),
  folderById: new Map(),
  listById: new Map(),
  statusById: new Map(),
  tagById: new Map(),
  statusesForList: () => [],
};

export function useWorkspace(): WorkspaceIndex {
  const data = useLiveQuery(async () => {
    const d = db();
    const [workspaces, spaces, folders, lists, statuses, tags] = await Promise.all([
      d.workspaces.toArray(),
      d.spaces.toArray(),
      d.folders.toArray(),
      d.lists.toArray(),
      d.statuses.toArray(),
      d.tags.toArray(),
    ]);
    return {
      workspaces: alive(workspaces),
      spaces: alive(spaces).sort((a, b) => a.position - b.position),
      folders: alive(folders).sort((a, b) => a.position - b.position),
      lists: alive(lists).sort((a, b) => a.position - b.position),
      statuses: alive(statuses),
      tags: alive(tags).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, []);
  return useMemo(() => {
    if (!data) return EMPTY;
    const listById = new Map(data.lists.map((l) => [l.id, l]));
    const cache = new Map<string, Status[]>();
    return {
      loaded: true,
      ...data,
      spaceById: new Map(data.spaces.map((s) => [s.id, s])),
      folderById: new Map(data.folders.map((f) => [f.id, f])),
      listById,
      statusById: new Map(data.statuses.map((s) => [s.id, s])),
      tagById: new Map(data.tags.map((t) => [t.id, t])),
      statusesForList: (listId: string) => {
        const hit = cache.get(listId);
        if (hit) return hit;
        const list = listById.get(listId);
        const res = list ? resolveListStatuses(list, data.statuses) : [];
        cache.set(listId, res);
        return res;
      },
    };
  }, [data]);
}

/** All live tasks (including completed); views filter in memory. Fine for tens of thousands. */
export function useAllTasks(): Task[] | undefined {
  return useLiveQuery(async () => alive(await db().tasks.toArray()), []);
}

export function useListTasks(listId: string | undefined): Task[] | undefined {
  return useLiveQuery(
    async () => (listId ? alive(await db().tasks.where('list_id').equals(listId).toArray()) : []),
    [listId],
  );
}

export function useTask(taskId: string | undefined): Task | null | undefined {
  return useLiveQuery(
    async () => (taskId ? ((await db().tasks.get(taskId)) ?? null) : null),
    [taskId],
  );
}

export function useSubtasks(taskId: string | undefined): Task[] | undefined {
  return useLiveQuery(
    async () =>
      taskId
        ? alive(await db().tasks.where('parent_task_id').equals(taskId).toArray()).sort(
            (a, b) => a.position - b.position,
          )
        : [],
    [taskId],
  );
}

/** taskId → tagIds for every live task-tag link. */
export function useTaskTagMap(): Map<string, string[]> {
  const rows = useLiveQuery(async () => alive(await db().task_tags.toArray()), []);
  return useMemo(() => {
    const m = new Map<string, string[]>();
    for (const r of rows ?? []) {
      const arr = m.get(r.task_id);
      if (arr) arr.push(r.tag_id);
      else m.set(r.task_id, [r.tag_id]);
    }
    return m;
  }, [rows]);
}

export function useTaskReminders(taskId: string | undefined): Reminder[] | undefined {
  return useLiveQuery(
    async () =>
      taskId
        ? alive(await db().reminders.where('task_id').equals(taskId).toArray()).sort(
            (a, b) => Date.parse(a.fire_at) - Date.parse(b.fire_at),
          )
        : [],
    [taskId],
  );
}

export function useUpcomingReminders(limit = 6): Reminder[] | undefined {
  return useLiveQuery(async () => {
    const rows = await db().reminders.where('state').anyOf('scheduled', 'snoozed').toArray();
    return alive(rows)
      .sort(
        (a, b) =>
          Date.parse(a.snoozed_until ?? a.fire_at) - Date.parse(b.snoozed_until ?? b.fire_at),
      )
      .slice(0, limit);
  }, [limit]);
}

export function useNotifications(): NotificationLog[] | undefined {
  return useLiveQuery(async () => {
    const rows = await db().notifications_log.orderBy('sent_at').reverse().limit(500).toArray();
    return alive(rows).filter((n) => n.channel !== 'email');
  }, []);
}

export function useUnreadCount(): number {
  return (
    useLiveQuery(async () => {
      const rows = await db().notifications_log.toArray();
      return rows.filter((n) => !n.deleted_at && !n.read_at && n.channel !== 'email').length;
    }, []) ?? 0
  );
}

export function useTaskActivity(taskId: string | undefined): TaskActivity[] | undefined {
  return useLiveQuery(
    async () =>
      taskId
        ? alive(await db().task_activity.where('task_id').equals(taskId).toArray()).sort(
            (a, b) => Date.parse(a.created_at) - Date.parse(b.created_at),
          )
        : [],
    [taskId],
  );
}

/** Keys (`table:key`) with local changes not yet on the server. */
export function usePendingKeys(): Set<string> {
  const rows = useLiveQuery(async () => db().outbox.toArray(), []);
  return useMemo(() => new Set((rows ?? []).map((r) => `${r.table}:${r.key}`)), [rows]);
}

export function usePendingCount(): number {
  return useLiveQuery(async () => db().outbox.count(), []) ?? 0;
}

export function useChecklists(taskId: string | undefined) {
  return useLiveQuery(async () => {
    if (!taskId) return [];
    const lists = alive(await db().checklists.where('task_id').equals(taskId).toArray()).sort(
      (a, b) => a.position - b.position,
    );
    const items = alive(
      await db()
        .checklist_items.where('checklist_id')
        .anyOf(lists.map((l) => l.id))
        .toArray(),
    );
    return lists.map((l) => ({
      ...l,
      items: items.filter((i) => i.checklist_id === l.id).sort((a, b) => a.position - b.position),
    }));
  }, [taskId]);
}

export function isTaskComplete(task: Task, statusById: Map<string, Status>): boolean {
  return isCompleteType(statusById.get(task.status_id)?.type);
}

/** "Space / List" for a task. */
export function taskLocation(task: Task, ws: WorkspaceIndex): { space?: Space; list?: List } {
  const list = ws.listById.get(task.list_id);
  return { list, space: list ? ws.spaceById.get(list.space_id) : undefined };
}
